import { forwardRef } from "react";
import { Link, useLocation, useNavigate, type LinkProps } from "react-router-dom";
import { localeFromPath, localizedPath, stripLocale } from "@/lib/locale";
import { setLandingLanguage, type LandingLang } from "@/lib/i18n-landing";

/**
 * Drop-in de react-router <Link> que prefija el `to` con el idioma actual
 * (derivado de la URL). Se usa vía `import { LocaleLink as Link }` en los
 * componentes del sitio público, de modo que TODA la navegación interna
 * mantiene el idioma sin tocar cada `<Link>` uno a uno: es = sin prefijo,
 * ca/en = /ca, /en. Los paths no localizables (/platform, externos…) pasan
 * intactos (ver localizedPath).
 */
export const LocaleLink = forwardRef<HTMLAnchorElement, LinkProps>(
  function LocaleLink({ to, ...rest }, ref) {
    const { pathname } = useLocation();
    const lang = localeFromPath(pathname);
    const localizedTo = typeof to === "string" ? localizedPath(to, lang) : to;
    return <Link ref={ref} to={localizedTo} {...rest} />;
  },
);

/**
 * Hook para el selector de idioma: persiste la preferencia (localStorage) y
 * navega a la URL equivalente de la página actual en el idioma destino
 * (mismo path, otro prefijo de locale). La URL sigue siendo la fuente de
 * verdad: <LangLayout> re-sincroniza i18n al cambiar de ruta.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook que comparte archivo con LocaleLink
export function useLanguageSwitcher() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (lang: LandingLang) => {
    setLandingLanguage(lang);
    navigate(localizedPath(stripLocale(pathname), lang));
  };
}
